import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { GridOptions, ColDef } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { AgContextMenuService } from 'src/app/reusables/services/ag-context-menu.service';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { AgentStateDetailComponent } from '../../modals/agent-state-detail/agent-state-detail.component';
import { TasksModalComponent } from '../../modals/tasks-modal/tasks-modal.component';
import { CurrentAgentStatesService } from '../current-agent-states/current-agent-states.service';
import { ContactListDetailsComponent } from './contact-list-details/contact-list-details.component';
import { ContactStateCellRendererComponent } from './contact-list-details/contact-state-cell-renderer/contact-state-cell-renderer.component';
import { ContactListImageCellRendererComponent } from './contact-list-image-cell-renderer/contact-list-image-cell-renderer.component';
import { ContactListObject, DetailedStateObj } from './contact-list.models';
import { ContactListService } from './contact-list.service';
import { AgGridAngular } from 'ag-grid-angular';
import { IModuleManagerMenuItem, ModuleManagerMenuItemType } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { SuggestCoachingModalComponent } from '../../modals/suggest-coaching-modal/suggest-coaching-modal.component';

@Component({
    selector: 'cv-contact-list',
    templateUrl: './contact-list.component.html',
    styleUrls: ['./contact-list.component.scss']
})

export class ContactListComponent implements OnInit, OnDestroy {
    @Input() moduleKey: string;
    @Input() private moduleManager: ModuleManager;
    // eslint-disable-next-line @typescript-eslint/member-ordering
    @ViewChild("searchBox", { read: ElementRef }) searchBox: ElementRef<HTMLInputElement>;
    @ViewChild('contactListWidgetGrid') agGrid: AgGridAngular;

    public showFilter = false;
    gridOptions: GridOptions;
    dataLoadSuccess = false;
    modalRef: NgbModalRef = null;
    filterValue = '';
    noData = false;
    //For cxone search placeholder localization
    searchPlaceholder = "";
    isAgentStateDetailModalOpen = false;
    
    private stateRestoreInProgress = false; // Flag to prevent state save while restoring state until the widget is "stable"
    private data: ContactListObject[] = []; // FIXME: When the endpoint properly sends over objects fit for the ag-grid, update this type
    private tableState: any;
    private gridReady = false;
    private params: any;
    private restoreInProgress = false;
    private stateSaveTimer: any;
    private isFirstLoad = true; // used for logging in the LoadData()
    private isDataLoading = false;
    private detailsLoading = false;
    private isCXOne = false;

    private subscriptions: Subscription[] = [];

    private cancelRefresh: () => void; // This function is used to resolve the cancellerRefresh promise in loadData()

    public isLimitNotificationShown = false;
    public isLimitNotificationClosed = false;
    public limitNotificationMessage =  "";
    public readonly MAX_WIDGET_LIMIT = 10000;

    constructor(
        private ngbModal: NgbModal,
        private cvLocalizationService: CVLocalizationService,
        private agGridHelperService: AgGridHelperService,
        private contactListService: ContactListService,
        private agContextMenuService: AgContextMenuService,
        private agentStateService: CurrentAgentStatesService,
        private navigationService: NavigationService,
        public config: CvConfigurationService,
    ) { }

    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting; };

    async ngOnInit() {
        this.isCXOne = this.config.isCxOne;

        await Promise.all([ // Load all the localization sections
            this.cvLocalizationService.GetSection('Contact_List'),
            this.cvLocalizationService.GetSection('Error_Module'),
            this.cvLocalizationService.GetSection('Common'),
            this.cvLocalizationService.GetSection('Widgets'),
        ]);

        this.limitNotificationMessage = this.cvLocalizationService.Format(
            this.cvLocalizationService.getCachedText("Widgets", "Limit_Notification_Message_No_Export", "Displaying the latest {0} records. Adjust filters for more precise results."),
            "10,000"
        );

        this.moduleManager.setOptions({
            name: 'Contact List', // sets the title of the module (defaults to the database entry ModuleName if unset)
            onSettingsApplied: () => this.loadData(false, true),
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.VeryShort,
            onRefresh: (isAutoRefresh: boolean) => {
                this.loadData(isAutoRefresh);
            },
            canRefresh: () => !this.isDataLoading, // Disable Refresh when there's already a data load in progress.
        });

        this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");

        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });

        const timeComparator = (num1: number, num2: number) => { // Comparator used in State Time and Contact Time columns for sorting to work correctly
            const num1Seconds = this.hmsToSeconds(num1);
            const num2Seconds = this.hmsToSeconds(num2);
            if (num1Seconds === null && num2Seconds === null) {
                return 0;
            }
            if (num1Seconds === null) {
                return -1;
            }
            if (num2Seconds === null) {
                return 1;
            }
            return num1Seconds - num2Seconds;
        };

        this.gridOptions = this.agGridHelperService.createGridOptions({
            context: { moduleManager: this.moduleManager },
            components: {
                ContactStateCellRendererComponent,
                ContactListImageCellRendererComponent,
                ClickableRenderer: this.agGridHelperService.createClickableGroupRenderer(
                    async (params: any) => {
                        if (this.detailsLoading) return;
                        this.detailsLoading = true;
                        const detailedData = await this.LoadDetailedData(params.data.ContactID);
                        this.openModal(params.data.Skill,
                            params.data.ContactID,
                            params.data.Group.groupName,
                            detailedData,);
                        this.detailsLoading = false;
                    }
                ),
                GroupDrillDownCellRenderer: this.agGridHelperService.GroupDrillDownCellRenderer,
            },
            // grid options
            accentedSort: true,
            animateRows: true,
            suppressCellFocus: true,
            suppressScrollOnNewData: true,
            suppressContextMenu: false,
            // grid events
            onGridReady: (event: any) => {
                this.gridReady = true;
                if (this.data) {
                    event.api.setGridOption('rowData', this.data);
                }

                this.stateRestoreInProgress = true; // Prevent saving while the widget is still loading the initial states

                this.restoreColumnState(); //Needed to restore columns size saved on the tableState.
                if (!this.tableState) {
                    this.agGridHelperService.sizeColumnsSmart(event); //This will trigger the publish bar only once if the gridState is null.
                    this.updateTableState();
                }

                this.constructColumnMenuItem();

                setTimeout(() => this.stateRestoreInProgress = false, 0); // Wait for the next tick to let events finish before allowing state save
            },
            onColumnVisible: event => {
                if (this.tableState == null || this.tableState.columns.map(c => c.width).filter(w => w != null).length === 0)
                    this.agGridHelperService.sizeColumnsSmart(event);
                this.updateTableState();
            },
            onFilterChanged: () => this.updateTableState(),
            getRowId: (params: any) => params.data.ContactID,
            onSortChanged: () => this.updateTableState(),
            onColumnMoved: () => this.updateTableState(),
            onColumnPinned: () => this.updateTableState(),
            onColumnResized: event => {
                if (event.source == "uiColumnResized") {
                    this.updateTableState();
                }
            },
            onCellClicked: (params: any) => this.params = params,
            onGridSizeChanged: event => {
                if (this.isResizeWaiting) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                    this.updateTableState();
                } else if (this.moduleManager.container.isFullscreen) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                } else {
                    this.restoreColumnState();
                }

                this.constructColumnMenuItem();
            },
            defaultColDef: {
                minWidth: this.agGridHelperService.defaultMinWidth,
            },
            // grid data
            columnDefs: [
                {
                    field: 'ContactID',
                    colId: 'ContactID',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Contact_ID', 'Contact ID'),
                    filter: 'agTextColumnFilter',
                    cellRenderer: 'ClickableRenderer',
                },
                {
                    colId: 'State',
                    field: "State",
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'State', 'State'),
                    cellRenderer: 'ContactStateCellRendererComponent',
                    // This value getter is so that ag-grid can detect when the value of the cell has changed,
                    // and so that StateCellRenderer.refresh() function gets called on such change.
                    valueGetter: params => `${params.data.State} ${params.data.StateTotal}`,
                    filter: 'agTextColumnFilter',
                },
                {
                    colId: 'State Time',
                    field: 'StateTotal',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'State_Time', 'State Time'),
                    filter: 'agTextColumnFilter',
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : "",
                    comparator: timeComparator,
                },
                {
                    field: 'Total',
                    colId: 'Total',
                    headerName: this.isCXOne ? this.cvLocalizationService.getCachedText('Contact_List', 'Contact_Time', 'Contact Time') :
                        this.cvLocalizationService.getCachedText('Contact_List', 'Total', 'Total'),
                    filter: 'agTextColumnFilter',
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : "",
                    comparator: timeComparator,
                },
                {
                    field: 'Group',
                    headerName: this.isCXOne ? this.cvLocalizationService.getCachedText('Contact_List', 'Agent_Name', 'Agent Name') :
                        this.cvLocalizationService.getCachedText('Contact_List', 'Agent', 'Agent'),
                    colId: 'Agent',
                    type: 'groupColumn',
                    cellRenderer: 'GroupDrillDownCellRenderer',
                    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => { // makes unassigned agents stay at the bottom when sorted
                        if (valueA === valueB) return 0;
                        // for null
                        else if (valueA === '<i>Unassigned</i>') {
                            return isInverted ? -1: 1;
                        }
                        else if (valueB === '<i>Unassigned</i>') {
                            return isInverted ? 1: -1;
                        }
                        else {
                            return (valueA.groupName > valueB.groupName) ? 1 : -1;
                        }
                    },
                    valueGetter: params => {
                        if (!params.node.group && params.node.data.Group.groupID === 0)
                            return `<i>${this.cvLocalizationService.getCachedText('Contact_List', 'Unassigned', 'Unassigned')}</i>`;
                        return params.data.Group;
                    },
                    getQuickFilterText: params => {
                        if (!params.value) { // if it is null filter on the value unassigned
                            return this.cvLocalizationService.getCachedText('Contact_List', 'Unassigned', 'Unassigned');
                        }
                        return params.value;
                    },
                    filterValueGetter: params => params.data.Group.groupName, // so we can filter on the agent name column since its an object
                    filter: 'agTextColumnFilter',
                },
                {
                    field: 'Skill',
                    colId: 'Skill',
                    headerName: this.isCXOne ? this.cvLocalizationService.getCachedText('Contact_List', 'Skill_Name', 'Skill Name') :
                        this.cvLocalizationService.getCachedText('Contact_List', 'Skill', 'Skill'),
                    filter: 'agTextColumnFilter',
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : "",
                },
                {
                    field: 'Campaign',
                    colId: 'Campaign',
                    headerName: this.isCXOne ? this.cvLocalizationService.getCachedText('Contact_List', 'Campaign_Name', 'Campaign Name') :
                        this.cvLocalizationService.getCachedText('Contact_List', 'Campaign', 'Campaign'),
                    filter: 'agTextColumnFilter',
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : "",
                },
                {
                    field: 'MediaType',
                    colId: 'MediaType',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Media_Type', 'Media Type'),
                    filter: 'agTextColumnFilter',
                    cellRenderer: this.config.isCxOne ? 'ContactListImageCellRendererComponent' : null,
                },
                {
                    field: 'CompanyNumber',
                    colId: 'CompanyNumber',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Company_Number', 'Company #'),
                    filter: 'agTextColumnFilter',
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : "",
                },
                {
                    field: 'CustomerNumber',
                    colId: 'CustomerNumber',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Customer_Number', 'Customer #'),
                    filter: 'agTextColumnFilter',
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : "",
                },
                {
                    field: "Direction",
                    colId: "Direction",
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Direction', 'Direction'),
                    filter: 'agTextColumnFilter',
                    cellRenderer: this.config.isCxOne ? 'ContactListImageCellRendererComponent' : null,
                },
                {
                    field: "MasterContactID",
                    colId: "MasterContactID",
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Master_Contact_ID', 'Master Contact ID'),
                    filter: 'agTextColumnFilter',
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : "",
                },
                {
                    field: 'HighProficiency',
                    colId: 'HighProficiency',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'High_Proficiency', 'High Proficiency'),
                    filter: 'agTextColumnFilter',
                    hide: true,
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : null,
                    suppressColumnsToolPanel: this.isCXOne ? false : true,
                },
                {
                    field: 'LowProficiency',
                    colId: 'LowProficiency',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Low_Proficiency', 'Low Proficiency'),
                    filter: 'agTextColumnFilter',
                    hide: true,
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : null,
                    suppressColumnsToolPanel: this.isCXOne ? false : true,
                },
                {
                    field: 'ProficiencyRoutingAttributeName',
                    colId: 'ProficiencyRoutingAttributeName',
                    headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Routing_Attribute', 'Routing Attribute'),
                    filter: 'agTextColumnFilter',
                    hide: true,
                    cellClass: this.isCXOne ? 'cxone-line-height-36' : null,
                    suppressColumnsToolPanel: this.isCXOne ? false : true,
                },

            ],
            rowData: [],
            overlayNoRowsTemplate: this.isCXOne ?
                `<div class="error-container" style="padding-top: 48px;">
                    <svg class="mb-2">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxone-error-text">
                        ${this.cvLocalizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
                    </p>
                </div>` : null,
            localeText: await this.agGridHelperService.getLocalizations(),
            suppressDragLeaveHidesColumns: true,
            suppressNoRowsOverlay: true,
        },
        { // second arument to CreateGridOptions
            enableCopy: true
        });

        if (this.config.isCxOne) {
            this.gridOptions.columnDefs.splice(8, 0, {
                colId: 'MediaSubtype', //Column was renamed to Channel but table state will still have it as MediaSubtype.
                field: 'Channel',
                headerName: this.cvLocalizationService.getCachedText('Contact_List', 'Channel', 'Channel'),
                filter: 'agTextColumnFilter',
                cellRenderer: 'ContactListImageCellRendererComponent',
                cellClass: this.isCXOne ? 'cxone-line-height-36' : "",
            });
        }

        this.subscriptions.push(this.agContextMenuService.agentDetailsClicked.subscribe( async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey())
                return;
            const response = await this.agentStateService.loadCurrentAgentStatesByGroupID(params.moduleKey, params.groupData.groupID);
            this.openAgentStateDetail(params.moduleKey, params.groupData.groupID, response);
        }));

        this.subscriptions.push(this.agContextMenuService.createTaskClicked.subscribe( async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            this.openCreateTaskModal(params.moduleKey, params.groupData.groupID, params.groupData);
        }));

        this.subscriptions.push(this.agContextMenuService.suggestCoachingClicked.subscribe(async params => {
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            this.openSuggestCoachingModal(params.moduleKey, params.groupData.groupID, params.groupData);
        }));

        this.moduleManager.addState({
            name: "tableState",
            getStateData: () => this.tableState,
            onStateLoaded: (stateData) => {
                if (!stateData) { // No table state loaded
                    return;
                }

                this.tableState = stateData;
                this.restoreColumnState();
            },
            autoLoad: true
        });
    }

    // Converts time values from hours-minutes-seconds (hms) to seconds (03:25:50 -> 12350)
    public hmsToSeconds(time) {
        const splitTime = time.split(':');
        let seconds = 0;
        let minutes = 1;

        while (splitTime.length > 0) {
            seconds += minutes * parseInt(splitTime.pop(), 10);
            minutes *= 60;
        }

        return seconds;
    }

    public applyFilter() {
        if (this.agGrid?.api) { // notify ag-grid that the filter changed
            this.agGrid?.api.setGridOption('quickFilterText', this.filterValue.trim());
            if (this.showFilter && this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0)
                this.agGrid?.api?.showNoRowsOverlay();
            else
                this.agGrid?.api?.hideOverlay();
        }
    }

    /**
     * Called when the clear button on the filter box is clicked.  Clears the filter
     */
    public clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    }

    /** Opens the Agent State Detail modal from the Context Menu on Groups column */
    openAgentStateDetail(moduleKey: string, groupID: number, selectedRow: any) {
        if (this.isAgentStateDetailModalOpen) return;
        this.isAgentStateDetailModalOpen = true;
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(AgentStateDetailComponent,
            { size: "xl", centered: true, modalDialogClass: this.config.isCxOne ? 'cx-one-ui' : null, scrollable: true });
        this.modalRef.result.finally(() => {
            this.navigationService.showOverlay = false;
            this.isAgentStateDetailModalOpen = false;
        });

        const component: AgentStateDetailComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    openCreateTaskModal(moduleKey: string, groupID: number, selectedRow: any) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(TasksModalComponent,
            {size : "xl", centered: true, scrollable: true, modalDialogClass: null});
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: TasksModalComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    openSuggestCoachingModal(moduleKey: string, groupID: number, selectedRow: any) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(SuggestCoachingModalComponent,
            { size: "md", centered: true, scrollable: true, modalDialogClass: null });
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: SuggestCoachingModalComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    moreOptionsEvent(e: any) {
        setTimeout(() => {
            if (this.params != null) {
                const rowIndex = this.params.rowIndex;
                const rowNode = this.params.node;
                const column = this.params.column;
                const mouseEvent = this.params.event;
                const value = this.params.value;
                this.params.api.contextMenuFactory.showMenu(rowNode, column, value, mouseEvent);
            }
        }, 500);
    }

    queueStateSave() {
        if (this.stateRestoreInProgress)
            return;

        if (this.stateSaveTimer !== false) {
            clearTimeout(this.stateSaveTimer);
        }

        this.stateSaveTimer = setTimeout(async () => {
            this.stateSaveTimer = false;
            if (await this.moduleManager.saveState('tableState')) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500);
    };

    /**
     * Retrieves the table state and saves it
     */
    updateTableState() {
        if (!this.agGrid?.api || this.restoreInProgress)
            return;

        // generate state from grid
        const tableState = this.agGridHelperService.getGridState(this.agGrid?.api);
        if (tableState != null) {
            this.tableState = tableState;
        }

        // tell module manager to save the state
        this.queueStateSave();
    };

    restoreColumnState() {
        if (!this.tableState|| !this.agGrid?.api) {
            return;
        }

        // keep state from being saved while restoring
        this.restoreInProgress = true;

        this.agGridHelperService.loadGridState(this.agGrid?.api, this.tableState);

        // states can be saved again
        this.restoreInProgress = false;
    };

    async loadData(isAutoRefresh = false, cancelRefresh = false) {
        if (this.isDataLoading && !cancelRefresh)
            return;
        this.isDataLoading = true;

        // If we didn't return early, cancel the previous data load.
        if (this.cancelRefresh) {
            this.cancelRefresh();
        }
        const cancellerPromise: Promise<void> = new Promise(resolve => this.cancelRefresh = () => resolve());

        this.moduleManager.showLoader = !isAutoRefresh;
        try {
            const response = await this.contactListService.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad, this.isCXOne, cancellerPromise);
            this.cancelRefresh = null;
            this.moduleManager.setAutoRefreshReady();
            this.data = response.data;
            this.noData = this.data.length === 0;

            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('rowData', this.data);
            }// Else, the table isn't ready, but onGridReady should set its data
        } catch (e) {
            if (e?.code === DOMException.ABORT_ERR || e?.statusText === 'abort') {
                return;
            }
            this.moduleManager.handleLoadError(e, isAutoRefresh);
        } finally {
            this.isDataLoading = false;
            if(!this.cancelRefresh) {
                this.applyFilter();
                this.dataLoadSuccess = !this.noData;
                this.moduleManager.showLoader = false;
                this.isFirstLoad = false;
            }
        }

        this.moduleManager.moduleOptions.nameSuffix = ` (${this.data?.length || 0})`;
        
        this.showLimitNotification();
    }

    showLimitNotification(): void {
        if (!this.isLimitNotificationClosed) {
            this.isLimitNotificationShown = this.data.length >= this.MAX_WIDGET_LIMIT;
        }
    }

    closeLimitNotification(): void {
        this.isLimitNotificationShown = false;
        this.isLimitNotificationClosed = true;
    }

    async LoadDetailedData(contactID: string): Promise<DetailedStateObj> {
        this.moduleManager.showLoader = true;
        const response = await this.contactListService.LoadDetailedData(contactID, this.moduleKey);

        return response;
    }

    openModal(skill: string,
        id: number,
        agent: string,
        data: DetailedStateObj
    ) {
        const modalRef = this.ngbModal.open(ContactListDetailsComponent, {size : "md",
            centered: true,
            modalDialogClass: this.config.isCxOne ? 'cx-one-ui' : null,
            scrollable: true});
        const componant: ContactListDetailsComponent = modalRef.componentInstance;
        componant.data = data;
        componant.skill = skill;
        componant.id = id;
        componant.agent = agent;
        this.moduleManager.showLoader = false;
        this.detailsLoading = false;
    };

    /**
     * Shows and hides the search/filter bar
     */
    private toggleOpenSearch() { // called by moduleManager.addHeaderItem for search bar.
        if (this.showFilter) {
            this.showFilter = false;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', true);
            this.clearFilter();
        } else {
            this.showFilter = true;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', false);
            this.focusOnSearch();
        }
    }

    /**
     * Change input focus to the search bar.
     */
    async focusOnSearch() {
        // The element won't be ready immediately, so the focus has to be done asynchronously
        setTimeout(() => {
            if (this.config.isCxOne) {
                const el = document.querySelector(`div.cxone-text-input input[name='cxoneInput-${this.moduleKey}']`);
                $(el).focus();
            }
            else {
                this.searchBox?.nativeElement?.focus();
            }
        }, 200);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => {
            sub.unsubscribe();
        });
    }

    constructColumnMenuItem() {
        if (this.agGrid?.api)
            this.addColumnMenuItem();
        else
            console.error("CurrentAgentStatesTable: constructColumnMenuItem() is being called outside of gridOptions");
    };

    // Function called when the menu item is checked/unchecked to hide/show columns.
    toggleColumnVisible(i) {
        const column: ColDef = this.gridOptions.columnDefs[i]; //column clicked
        const tableStateMatch = this.tableState.columns.find(c => c.name === column.colId); //column on table
        if (tableStateMatch) {
            column.hide = !tableStateMatch.visible;
        }
        const isHidden = !tableStateMatch.visible;

        // toggle column visibility
        this.agGrid?.api.setColumnVisible(tableStateMatch.name, isHidden);

        // update table state immediately to prevent UI delay (instead of queuing)
        this.prepareTableState();
        this.queueStateSave();
    }

    prepareTableState() {
        if (!this.agGrid?.api)
            return;

        // generate state from grid
        const tableState = this.agGridHelperService.getGridState(this.agGrid.api);
        if (tableState) {
            this.tableState = tableState;
        }
    }

    addColumnMenuItem() {
        this.moduleManager.addMenuItem({
            name: this.cvLocalizationService.getCachedText("Contact_List", "Columns", "Columns"),
            icon: "columns",
            getEnabled: () => this.gridOptions.columnDefs.length > 0 && !this.noData,
            type: ModuleManagerMenuItemType.CheckMenu,
            getCheckItems: () => this.gridOptions.columnDefs,
            getItemName: i => {
                const colState = this.gridOptions.columnDefs[i] as ColDef;
                const column = this.agGrid?.api.getColumn(colState.colId);

                if (column) // during column changes the state can have columns that are no longer in the list
                    return column['colDef'].headerName;
                else
                    return "";
            },
            isItemChecked: i => {
                if (this.gridReady && !this.tableState) {
                    this.prepareTableState();
                }
                const columnDefinition: ColDef = this.gridOptions.columnDefs[i];

                // Since the index values may be out of order if columns are reordered,
                // find the correct table state by name/field instead of relying on the index.
                const tableStateMatch = this.tableState.columns.find(c => c.name === columnDefinition.colId);
                if (tableStateMatch) {
                    columnDefinition.hide = !tableStateMatch.visible;
                }
                return !columnDefinition.hide;
            },
            onCheckClicked: i => this.toggleColumnVisible(i),
        } as IModuleManagerMenuItem);
    }
}
